// LH-2 — the Library drawer, on the theme. Nothing it could do was taken away:
// upload, connect a folder on this computer and browse it, collections,
// search, edit, star, delete, pick. All of it kept, re-dressed in tokens.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DockPanel from '@/components/shell/DockPanel';
import { useNavigate } from 'react-router-dom';
import { makeUnifiedPayload, setUnifiedDragPayload } from '../utils/keymadeItemPayload';
import '../styles/library-hub.css';
import '../styles/assemblies.css';   // the Undo bar is the Assembly Sheet's own

const API = process.env.REACT_APP_BACKEND_URL;

const monthStart = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};

const picture = (url) => (!url || url.startsWith('http') || url.startsWith('data:')
  ? url : `${API}${url}`);

const openIDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open('KeyMadeLibrary', 1);
  req.onupgradeneeded = () => {
    const db = req.result;
    if (!db.objectStoreNames.contains('folders')) {
      db.createObjectStore('folders', { keyPath: 'id' });
    }
  };
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

export default function LibraryHub({ isOpen, onClose, mode = 'browse', label = '',
  onItemSelect }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ collections: {}, kinds: {} });
  const [collections, setCollections] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('all');          // all | favourites | month | collection
  const [collection, setCollection] = useState(null);
  const [kind, setKind] = useState(null);           // null = All, '' = Unsorted
  const [staging, setStaging] = useState(0);
  const [newCollection, setNewCollection] = useState(null);
  const [editing, setEditing] = useState(null);
  const [undo, setUndo] = useState(null);
  const [pending, setPending] = useState(null);     // files waiting for a kind
  const [folders, setFolders] = useState([]);
  const [browsing, setBrowsing] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [folderPath, setFolderPath] = useState([]);
  const [folderBusy, setFolderBusy] = useState(false);
  const chooser = useRef(null);
  const typing = useRef(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ limit: '500' });
    if (search.trim()) params.set('search', search.trim());
    if (kind) params.set('category', kind);
    if (view === 'favourites') params.set('favourites', '1');
    if (view === 'month') params.set('since', monthStart());
    if (view === 'collection' && collection) params.set('collection_id', collection);
    try {
      const res = await axios.get(`${API}/api/library/items?${params}`);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setCounts({ collections: res.data?.collections_count || {},
                  kinds: res.data?.kinds_count || {} });
    } catch (e) { toast.error('Could not read the Library'); }
  }, [search, kind, view, collection]);

  useEffect(() => {
    if (!isOpen) return;
    load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return;
    axios.get(`${API}/api/library/collections`)
      .then((res) => setCollections(res.data?.collections || [])).catch(() => {});
    axios.get(`${API}/api/library-kinds`)
      .then((res) => setKinds((res.data?.kinds || []).filter((k) => k.status === 'active')))
      .catch(() => {});
    axios.get(`${API}/api/unsorted`)
      .then((res) => setStaging((res.data?.items || []).length)).catch(() => {});
    (async () => {
      try {
        const db = await openIDB();
        const store = db.transaction('folders', 'readonly').objectStore('folders');
        const req = store.getAll();
        req.onsuccess = () => setFolders(req.result || []);
      } catch (e) { /* this computer has nothing connected */ }
    })();
  }, [isOpen]);

  // ── the picture a firm keeps, edited ──────────────────────────────────────
  const saveItem = (patch, now) => {
    setEditing((was) => ({ ...was, ...patch }));
    const send = async () => {
      try {
        await axios.put(`${API}/api/library/items/${editing.id}`, patch);
        load();
      } catch (e) { toast.error('Could not save that'); }
    };
    clearTimeout(typing.current);
    if (now) send(); else typing.current = setTimeout(send, 600);
  };

  const star = async (item) => {
    try {
      const res = await axios.post(`${API}/api/library/items/${item.id}/favourite`);
      setItems((rows) => rows.map((r) => (r.id === item.id ? res.data : r)));
      if (editing?.id === item.id) setEditing(res.data);
    } catch (e) { toast.error('Could not star that'); }
  };

  // The ten seconds are the undo: nothing is deleted until they pass.
  const removeItem = (item) => {
    setEditing(null);
    setItems((rows) => rows.filter((r) => r.id !== item.id));
    clearTimeout(timer.current);
    setUndo({ item, left: 10 });
    timer.current = setInterval(() => {
      setUndo((was) => {
        if (!was) return null;
        if (was.left > 1) return { ...was, left: was.left - 1 };
        clearInterval(timer.current);
        axios.delete(`${API}/api/library/items/${was.item.id}`).then(load).catch(() => {});
        return null;
      });
    }, 1000);
  };

  const putBack = () => {
    clearInterval(timer.current);
    setUndo(null);
    load();
  };

  // ── upload, through the kind strip ────────────────────────────────────────
  const chosen = (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith('image/'));
    if (!list.length) return;
    setPending({ files: list, kind: kinds[0]?.name || '' });
  };

  const sendPending = async () => {
    const { files, kind: into } = pending;
    setPending(null);
    let added = 0;
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      form.append('name', file.name.replace(/\.[^.]+$/, ''));
      form.append('source', 'Upload');
      if (into) form.append('category', into);
      try {
        await axios.post(`${API}/api/library/upload`, form);
        added += 1;
      } catch (e) { /* the next one may still land */ }
    }
    toast.success(`Kept ${added} of ${files.length} in the Library`);
    load();
  };

  // ── a folder on this computer ────────────────────────────────────────────
  const connectFolder = async () => {
    if (!window.showDirectoryPicker) {
      toast.error('This browser cannot open a folder. Use Upload.');
      return;
    }
    try {
      const handle = await window.showDirectoryPicker({ mode: 'read' });
      const folder = { id: `folder-${Date.now()}`, name: handle.name, handle };
      setFolders((was) => [...was, folder]);
      try {
        const db = await openIDB();
        db.transaction('folders', 'readwrite').objectStore('folders').put(folder);
      } catch (e) { /* it still works for this session */ }
      browseFolder(folder);
    } catch (e) { /* the picker was closed */ }
  };

  const browseFolder = async (folder, subHandle = null, pathEntry = null) => {
    setFolderBusy(true);
    setBrowsing(folder);
    const handle = subHandle || folder.handle;
    try {
      const permission = await handle.queryPermission({ mode: 'read' });
      if (permission !== 'granted'
          && await handle.requestPermission({ mode: 'read' }) !== 'granted') {
        toast.error('Permission denied');
        setFolderBusy(false);
        return;
      }
    } catch (e) {
      toast.error('Cannot read that folder — connect it again');
      setFolderBusy(false);
      return;
    }
    if (pathEntry) setFolderPath((was) => [...was, pathEntry]);
    else if (!subHandle) setFolderPath([{ name: folder.name, handle }]);

    const found = [];
    try {
      for await (const entry of handle.values()) {
        if (entry.kind === 'directory') {
          found.push({ kind: 'directory', name: entry.name, handle: entry });
        } else {
          const file = await entry.getFile();
          const ending = file.name.split('.').pop().toLowerCase();
          if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg', 'tiff'].includes(ending)) {
            found.push({ kind: 'file', name: file.name, file,
                         thumbnailUrl: URL.createObjectURL(file) });
          }
        }
      }
    } catch (e) { toast.error('Error reading that folder'); }
    found.sort((a, b) => (a.kind !== b.kind
      ? (a.kind === 'directory' ? -1 : 1) : a.name.localeCompare(b.name)));
    setFolderFiles(found);
    setFolderBusy(false);
  };

  const dragStart = (event, item) => {
    event.dataTransfer.setData('application/library-item', JSON.stringify(item));
    try {
      const type = (() => {
        const kindOf = (item.item_type || '').toLowerCase();
        if (kindOf === 'color') return 'color';
        if (['material', 'texture', 'hardware'].includes(kindOf)) return 'material';
        if (kindOf === 'object') return 'file';
        return 'image';
      })();
      setUnifiedDragPayload(event, makeUnifiedPayload({
        item_id: item.id, source_store: 'library_items', type, name: item.name,
        thumbnail: item.image_url,
        source_url: (item.metadata && item.metadata.source_url) || item.source_url || null,
        spec_num: null,
      }));
    } catch (e) { /* the legacy payload is already set */ }
    event.dataTransfer.effectAllowed = 'copy';
  };

  if (!isOpen) return null;

  const named = (id) => (collections.find((c) => c.id === id) || {}).name;
  const unsorted = items.some((i) => !kinds.some((k) => k.name === i.category));
  const shown = kind === '' ? items.filter(
    (i) => !kinds.some((k) => k.name === i.category)) : items;

  const pickThis = (item) => {
    onItemSelect?.(item);
    onClose?.();
  };

  const rail = (
    <nav className="klh-nav" data-testid="library-rail">
      <div className="klh-grp">
        <div className="klh-k">Library</div>
        {[['all', 'All pictures', total],
          ['favourites', 'Favourites', null],
          ['month', 'Added this month', null]].map(([id, text, count]) => (
          <button type="button" key={id}
            className={`klh-r${view === id ? ' on' : ''}`}
            onClick={() => { setView(id); setCollection(null); }}
            data-testid={`library-view-${id}`}>
            <span>{text}</span>
            {count != null ? <i>{count}</i> : null}
          </button>
        ))}
      </div>

      <div className="klh-grp">
        <div className="klh-k">Collections</div>
        {collections.map((c) => (
          <button type="button" key={c.id}
            className={`klh-r${collection === c.id ? ' on' : ''}`}
            onClick={() => { setView('collection'); setCollection(c.id); }}
            data-testid={`library-collection-${c.id}`}>
            <span>{c.name}</span>
            <i>{counts.collections[c.id] || 0}</i>
          </button>
        ))}
        {newCollection === null ? (
          <button type="button" className="klh-r add" onClick={() => setNewCollection('')}
            data-testid="library-collection-new">+ New collection</button>
        ) : (
          <input className="klh-in" value={newCollection} autoFocus
            placeholder="Name it…"
            onChange={(e) => setNewCollection(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Escape') setNewCollection(null);
              if (e.key !== 'Enter' || !newCollection.trim()) return;
              try {
                await axios.post(`${API}/api/library/collections`,
                  { name: newCollection.trim() });
                setNewCollection(null);
                const res = await axios.get(`${API}/api/library/collections`);
                setCollections(res.data?.collections || []);
              } catch (err) { toast.error('Could not make that collection'); }
            }}
            data-testid="library-collection-new-input" />
        )}
      </div>

      <div className="klh-grp">
        <div className="klh-k">Also in</div>
        <button type="button" className="klh-r"
          onClick={() => { onClose?.(); navigate('/work-table'); }}
          data-testid="library-goto-staging">
          <span>Staging</span><i>{staging}</i>
        </button>
        <button type="button" className="klh-r"
          onClick={() => { onClose?.(); navigate('/clips'); }}
          data-testid="library-goto-clips">
          <span>Material Library</span>
        </button>
      </div>

      {folders.length ? (
        <div className="klh-grp">
          <div className="klh-k">This computer</div>
          {folders.map((f) => (
            <button type="button" key={f.id}
              className={`klh-r${browsing?.id === f.id ? ' on' : ''}`}
              onClick={() => browseFolder(f)}
              data-testid={`library-folder-${f.id}`}>
              <span>{f.name}</span>
            </button>
          ))}
          {browsing ? (
            <button type="button" className="klh-r add"
              onClick={() => { setBrowsing(null); setFolderFiles([]); setFolderPath([]); }}
              data-testid="library-folder-close">Back to the Library</button>
          ) : null}
        </div>
      ) : null}
    </nav>
  );

  const strip = pending ? (
    <div className="klh-strip" data-testid="library-kind-strip">
      <span className="klh-k">Kind for these {pending.files.length} pictures</span>
      {kinds.map((k) => (
        <button type="button" key={k.id}
          className={`klh-chip${pending.kind === k.name ? ' on' : ''}`}
          onClick={() => setPending({ ...pending, kind: k.name })}
          data-testid={`library-strip-kind-${k.name}`}>{k.name}</button>
      ))}
      <button type="button" className="klh-btn brand" onClick={sendPending}
        data-testid="library-strip-save">Add to Library</button>
      <button type="button" className="klh-btn quiet" onClick={() => setPending(null)}
        data-testid="library-strip-cancel">Cancel</button>
    </div>
  ) : null;

  const main = browsing ? (
    <section className="klh-main" data-testid="library-folder-view">
      <div className="klh-crumb">
        {folderPath.map((p, index) => (
          <React.Fragment key={index}>
            {index > 0 ? <span>·</span> : null}
            <button type="button"
              className={index === folderPath.length - 1 ? 'here' : undefined}
              onClick={() => {
                setFolderPath(folderPath.slice(0, index + 1));
                browseFolder(browsing, p.handle);
              }}>{p.name}</button>
          </React.Fragment>
        ))}
      </div>
      {strip}
      <div className="klh-grid">
        {folderBusy ? <div className="klh-empty">Reading that folder…</div> : null}
        {folderFiles.filter((f) => f.kind === 'directory').map((f) => (
          <button type="button" key={f.name} className="klh-tile"
            onClick={() => browseFolder(browsing, f.handle,
              { name: f.name, handle: f.handle })}
            data-testid={`library-subfolder-${f.name}`}>
            <span className="swatch">/</span>
            <span className="nm"><span>{f.name}</span><span className="klh-k">Folder</span></span>
          </button>
        ))}
        {folderFiles.filter((f) => f.kind === 'file').map((f) => (
          <div className="klh-tile" key={f.name}>
            <img src={f.thumbnailUrl} alt="" loading="lazy" />
            <span className="nm"><span>{f.name}</span></span>
            <span className="ov">
              <button type="button" className="use"
                onClick={() => setPending({ files: [f.file], kind: kinds[0]?.name || '' })}
                data-testid={`library-folder-add-${f.name}`}>Add to Library</button>
              <button type="button"
                onClick={() => setPending({
                  files: folderFiles.filter((x) => x.kind === 'file').map((x) => x.file),
                  kind: kinds[0]?.name || '' })}
                data-testid="library-folder-add-all">Add all</button>
            </span>
          </div>
        ))}
      </div>
    </section>
  ) : (
    <section className="klh-main"
      onDragOver={(e) => { e.preventDefault(); }}
      onDrop={(e) => { e.preventDefault(); chosen(e.dataTransfer.files); }}>
      <div className="klh-flt">
        <span className="klh-k">Kind</span>
        <button type="button" className={`klh-chip${kind === null ? ' on' : ''}`}
          onClick={() => setKind(null)} data-testid="library-kind-all">All</button>
        {kinds.map((k) => (
          <button type="button" key={k.id}
            className={`klh-chip${kind === k.name ? ' on' : ''}`}
            onClick={() => setKind(k.name)} data-testid={`library-kind-${k.name}`}>
            {k.name}{counts.kinds[k.name] ? ` [${counts.kinds[k.name]}]` : ''}
          </button>
        ))}
        {unsorted ? (
          <button type="button" className={`klh-chip${kind === '' ? ' on' : ''}`}
            onClick={() => setKind('')} data-testid="library-kind-unsorted">Unsorted</button>
        ) : null}
        <span className="right">Newest first</span>
      </div>
      {strip}
      {!shown.length ? (
        <div className="klh-empty" data-testid="library-empty">
          {total === 0 ? (
            <>
              <b>Nothing in the Library yet.</b>
              <span>
                Pictures saved from websites land in Staging. Press Keep on a
                Staging card, or Upload here.
              </span>
            </>
          ) : (
            <>
              <b>Nothing here under that kind.</b>
              <span>Try All, or search.</span>
            </>
          )}
        </div>
      ) : (
        <div className="klh-grid" data-testid="library-grid">
          {shown.map((item) => (
            <div className="klh-tile" key={item.id} draggable
              onDragStart={(e) => dragStart(e, item)}
              data-testid={`library-tile-${item.id}`}>
              {item.image_url ? (
                <img src={picture(item.image_url)} alt="" loading="lazy"
                  onClick={() => setEditing(item)} />
              ) : (
                <span className="swatch" onClick={() => setEditing(item)}
                  style={item.color ? { background: item.color } : undefined}>
                  {(item.name || '?').slice(0, 1).toUpperCase()}
                </span>
              )}
              {(item.favourites || []).length ? (
                <span className="star" data-testid={`library-star-${item.id}`}>
                  ★ {(item.favourites || []).length}
                </span>
              ) : null}
              <button type="button" className="nm" onClick={() => setEditing(item)}
                data-testid={`library-open-${item.id}`}>
                <span>{item.name}</span>
                <span className="klh-k">
                  {[item.category, named(item.collection_id)].filter(Boolean).join(' · ')}
                </span>
              </button>
              <span className="ov">
                <button type="button" onClick={() => setEditing(item)}
                  data-testid={`library-overlay-open-${item.id}`}>Open</button>
                {mode === 'pick' ? (
                  <button type="button" className="use" onClick={() => pickThis(item)}
                    data-testid={`library-use-${item.id}`}>Use here</button>
                ) : (
                  <button type="button" onClick={() => star(item)}
                    data-testid={`library-fav-${item.id}`}>★</button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  const editor = editing ? (
    <section className="klh-ed" data-testid="library-editor">
      <div className="hd">
        <div>
          <div className="klh-k">Picture</div>
          <div className="klh-t">{editing.name}</div>
        </div>
        <button type="button" className="klh-btn quiet" onClick={() => setEditing(null)}
          data-testid="library-editor-done">Done</button>
      </div>
      <div className="bd">
        {editing.image_url ? <img src={picture(editing.image_url)} alt="" /> : null}
        <div className="klh-f">
          <label htmlFor="klh-name">Name</label>
          <input id="klh-name" className="klh-in" value={editing.name || ''}
            onChange={(e) => saveItem({ name: e.target.value })}
            onBlur={(e) => saveItem({ name: e.target.value }, true)}
            data-testid="library-editor-name" />
        </div>
        <div className="klh-f">
          <label>Kind</label>
          <div className="klh-flt" style={{ padding: 0, border: 0 }}>
            {kinds.map((k) => (
              <button type="button" key={k.id}
                className={`klh-chip${editing.category === k.name ? ' on' : ''}`}
                onClick={() => saveItem({ category: k.name }, true)}
                data-testid={`library-editor-kind-${k.name}`}>{k.name}</button>
            ))}
          </div>
        </div>
        <div className="klh-f">
          <label htmlFor="klh-col">Collection</label>
          <input id="klh-col" className="klh-in" list="klh-collections"
            defaultValue={named(editing.collection_id) || ''}
            placeholder="Choose or type a new one…"
            onBlur={async (e) => {
              const typed = e.target.value.trim();
              const known = collections.find(
                (c) => (c.name || '').toLowerCase() === typed.toLowerCase());
              if (known) { saveItem({ collection_id: known.id }, true); return; }
              if (!typed) { saveItem({ collection_id: null }, true); return; }
              try {
                const res = await axios.post(`${API}/api/library/collections`,
                  { name: typed });
                const made = res.data?.id || res.data?.collection?.id;
                const all = await axios.get(`${API}/api/library/collections`);
                setCollections(all.data?.collections || []);
                saveItem({ collection_id: made }, true);
              } catch (err) { toast.error('Could not make that collection'); }
            }}
            data-testid="library-editor-collection" />
          <datalist id="klh-collections">
            {collections.map((c) => <option key={c.id} value={c.name} />)}
          </datalist>
        </div>
        <div className="klh-f">
          <label htmlFor="klh-tags">Tags</label>
          <input id="klh-tags" className="klh-in"
            defaultValue={(editing.tags || []).join(', ')}
            placeholder="comma separated"
            onBlur={(e) => saveItem({ tags: e.target.value.split(',')
              .map((t) => t.trim()).filter(Boolean) }, true)}
            data-testid="library-editor-tags" />
        </div>
        <div className="klh-f">
          <label htmlFor="klh-notes">Notes</label>
          <textarea id="klh-notes" className="klh-in" rows={3}
            value={editing.notes || ''}
            onChange={(e) => saveItem({ notes: e.target.value })}
            onBlur={(e) => saveItem({ notes: e.target.value }, true)}
            data-testid="library-editor-notes" />
        </div>
        <div className="klh-said">
          <b>Source</b>
          <span>
            {editing.source === 'Staging' && editing.source_title
              ? `Staging · ${editing.source_title}` : (editing.source || 'Upload')}
          </span>
          <b>Added</b><span>{String(editing.created_at || '').slice(0, 10)}</span>
          <b>Used</b><span>{editing.use_count || 0}</span>
        </div>
      </div>
      <div className="ft">
        {mode === 'pick' ? (
          <button type="button" className="klh-btn brand" onClick={() => pickThis(editing)}
            data-testid="library-editor-use">Use here</button>
        ) : null}
        {editing.source_url ? (
          <a className="klh-btn" href={editing.source_url} target="_blank"
            rel="noopener noreferrer" data-testid="library-editor-source">Open source</a>
        ) : null}
        <button type="button" className="klh-btn" onClick={() => star(editing)}
          data-testid="library-editor-fav">★ Favourite</button>
        <button type="button" className="klh-btn quiet" onClick={() => removeItem(editing)}
          data-testid="library-editor-remove">Remove from Library</button>
      </div>
    </section>
  ) : null;

  // SH-2 — the last right-side panel to leave its own layer: it docks beside the
  // page like Connect and the contact panel, so nothing is covered.
  return (
    <DockPanel onClose={onClose} width="620px">
      <aside className="klh" data-testid="library-hub-drawer" data-mode={mode}>
        <div className="klh-hd">
          <div>
            <div className="klh-k">{mode === 'pick' ? label : 'Work Table · Library'}</div>
            <div className="klh-t">Library</div>
          </div>
          <div className="klh-tools">
            <input className="klh-srch" value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, tag, maker…" data-testid="library-search" />
            <button type="button" className="klh-btn" onClick={() => chooser.current?.click()}
              data-testid="library-upload">Upload</button>
            <input type="file" ref={chooser} accept="image/*" multiple
              style={{ display: 'none' }}
              onChange={(e) => { chosen(e.target.files); e.target.value = ''; }}
              data-testid="library-file-upload" />
            <button type="button" className="klh-btn quiet" onClick={connectFolder}
              title="Connect a folder on this computer"
              data-testid="connect-folder-btn">Folder</button>
            <button type="button" className="klh-btn quiet" onClick={onClose}
              data-testid="library-close">{mode === 'pick' ? 'Done' : 'Close'}</button>
          </div>
        </div>
        <div className="klh-body">
          {rail}
          {editor || main}
        </div>
        {undo ? (
          <div className="kas-undo show" data-testid="library-undo">
            <span>Removed · {undo.item.name} · {undo.left}s</span>
            <button type="button" className="btn" onClick={putBack}
              data-testid="library-undo-button">Undo</button>
          </div>
        ) : null}
      </aside>
    </DockPanel>
  );
}
